import { BehaviorSubject, catchError, Observable, of, tap, throwError } from 'rxjs';
import { GetAllPayerListResult, Payer, PayerExportResult } from '@app/core/models/payer.model';
import {
  GetAllPayersRequest,
  GetAllProviderRequest,
  OrderByEnum,
  ProviderSortColumnEnum
} from '@app/core/models/requests';
import { GetGroupInfoResponse, PaginatedResponse } from '@app/core/models/responses';
import { HttpMethod, HttpService } from '../http/http.service';
import { HttpParams, HttpResponse } from '@angular/common/http';

import { APIConstants } from '@app/core/constants/api.constants';
import { APIResponse } from '@app/core/models/api.model';
import { AuthService } from '../auth/auth.service';
import { EmbedAuthService } from '../auth/embed-auth.service';
import { EmbedMessageToParentStatus } from '@app/core/models/embed.model';
import { EnhancedPayer } from '@app/core/models/eligibility-complete.model';
import { Injectable } from '@angular/core';
import { ProcedureCode } from '@app/core/models/procedure-code.model';
import { Provider } from '@app/core/models/provider.model';
import { SharedMethods } from '@app/shared/utils/shared-methods';
import { Tag } from '@app/core/models/appointment.model';
import { DXCPortal } from '@app/core/models/portal.model';
import { DXCMFAServiceType, GroupMFAType } from '@app/core/models/administration.model';

@Injectable({
  providedIn: 'root'
})
export class SharedService {
  private _providersCache: Map<string, HttpResponse<APIResponse<Provider[]>>> = new Map();
  private _providers$ = new BehaviorSubject<Provider[]>([]);
  private _tags$ = new BehaviorSubject<Tag[]>([]);
  private _payers$ = new BehaviorSubject<Payer[]>([]);
  private _procedureCodes$ = new BehaviorSubject<ProcedureCode[]>([]);
  modalClosed$ = new BehaviorSubject<{ id: number; closed: boolean }>({
    id: 0,
    closed: true
  });
  private groupToDXCMFAMap: Record<GroupMFAType, DXCMFAServiceType> = {
    [GroupMFAType.Email]: DXCMFAServiceType.DXCEMail,
    [GroupMFAType.SMS]: DXCMFAServiceType.DXCSms
  };

  private dxcToGroupMFAMap: Record<DXCMFAServiceType, GroupMFAType> = {
    [DXCMFAServiceType.DXCEMail]: GroupMFAType.Email,
    [DXCMFAServiceType.DXCSms]: GroupMFAType.SMS
  };
  constructor(
    private http: HttpService,
    private authService: AuthService,
    private embedAuthService: EmbedAuthService
  ) {}

  getAllPayers(args?: GetAllPayersRequest) {
    let params = new HttpParams();
    const cache = args?.cache ?? true;

    if (args) {
      const { supported, page, limit: limit } = args;

      if (supported) {
        params = params.set('supported', supported);
      }

      if (page && limit) {
        params = params.set('page', page);
        params = params.set('limit', limit);
      }
    }

    return this.http
      .sendSecureRequest<Payer[]>({
        method: HttpMethod.GET,
        url: this.embedAuthService.isEmbedded()
          ? APIConstants.externalApiEmbedPayers
          : APIConstants.getAllPayers,
        cache,
        params
      })
      .pipe(
        tap((response) => {
          if (response.ok && response.body) {
            this._payers$.next(response.body.data);
          }
        }),
        catchError((error) => {
          if (this.embedAuthService.isEmbedded())
            SharedMethods.checkAndSendMessageToParent(EmbedMessageToParentStatus.Error);
          return throwError(() => error);
        })
      );
  }

  getAllAvailablePayers() {
    return this.http.sendSecureRequest<Payer[]>({
      method: HttpMethod.GET,
      url: this.embedAuthService.isEmbedded()
        ? APIConstants.externalApiEmbedAvailablePayers
        : APIConstants.getAllAvailablePayers
    });
  }

  getPayerList({ page, limit, search }: { page: number; limit: number; search?: string }) {
    let params = new HttpParams({ fromObject: { page, limit } });

    if (search) {
      params = params.set('search', search);
    }

    if (page && limit) {
      params = params.set('page', page);
      params = params.set('limit', limit);
    }

    return this.http.sendRequest<GetAllPayerListResult>({
      method: HttpMethod.GET,
      url: APIConstants.getPayerList,
      params
    });
  }

  getPayerListExport(type: string) {
    let params = new HttpParams();
    params = params.set('type', type);

    return this.http.sendRequest<PayerExportResult>({
      method: HttpMethod.GET,
      url: APIConstants.getPayerListExport,
      params
    });
  }

  getAllEnhancedPayers() {
    return this.http.sendSecureRequest<EnhancedPayer[]>({
      method: HttpMethod.GET,
      url: APIConstants.getAllEnhancedPayers
    });
  }

  getGroupInfo() {
    return this.http.sendSecureRequest<GetGroupInfoResponse>({
      method: HttpMethod.GET,
      url: this.embedAuthService.isEmbedded()
        ? APIConstants.externalApiEmbedGetGroupInfo
        : APIConstants.getGroupInfo,
      cache: true,
      params: new HttpParams({ fromObject: { groupId: this.authService.getCurrentLocation() } })
    });
  }

  private getProviderCacheKey() {
    const locationId = this.authService.getCurrentLocation();
    return `PROVIDERS_${locationId != null ? locationId.toString() : 'default'}`;
  }

  get providers$() {
    return this._providers$.asObservable();
  }

  refreshProviders() {
    return this.http
      .sendSecureRequest<Provider[]>({
        method: HttpMethod.POST,
        url: this.embedAuthService.isEmbedded()
          ? APIConstants.externalApiEmbedProviders
          : APIConstants.providers,
        body: {
          search: '',
          sortBy: ProviderSortColumnEnum.LAST_NAME,
          orderBy: OrderByEnum.ASC.toLowerCase()
        }
      })
      .subscribe((response) => {
        if (response.ok && response.body) {
          this._providers$.next(response.body.data);
        }
      });
  }

  getAllProviders({ page, limit, search, sortBy, orderBy }: GetAllProviderRequest) {
    return this.http.sendSecureRequest<PaginatedResponse<Provider>>({
      method: HttpMethod.POST,
      url: this.embedAuthService.isEmbedded()
        ? APIConstants.externalApiEmbedProviders
        : APIConstants.providers,
      body: {
        page: page,
        limit: limit,
        search: search,
        sortBy: sortBy,
        orderBy: orderBy?.toLowerCase()
      }
    });
  }

  get payers$() {
    return this._payers$.asObservable();
  }

  getProvidersList() {
    // const cacheKey = this.getProviderCacheKey();

    // if (this._tags.has(cacheKey)) {
    //   return of(this._tags.get(cacheKey) as HttpResponse<APIResponse<Provider[]>>);
    // }

    return this.http
      .sendSecureRequest<PaginatedResponse<Provider>>({
        method: HttpMethod.POST,
        url: this.embedAuthService.isEmbedded()
          ? APIConstants.externalApiEmbedProviders
          : APIConstants.providers,
        body: {
          search: '',
          sortBy: ProviderSortColumnEnum.LAST_NAME,
          orderBy: OrderByEnum.ASC.toLowerCase()
        }
      })
      .pipe(
        tap((response) => {
          if (response.ok && response.body) {
            // this._providersCache.set(cacheKey, response);
            this._providers$.next(response.body.data.results);
          }
        }),
        catchError((error) => {
          if (this.embedAuthService.isEmbedded())
            SharedMethods.checkAndSendMessageToParent(EmbedMessageToParentStatus.Error);
          return throwError(() => error);
        })
      );
  }

  getTagsListForce() {
    return this.http
      .sendSecureRequest<Tag[]>({
        method: HttpMethod.GET,
        url: this.embedAuthService.isEmbedded()
          ? APIConstants.externalApiEmbedTags
          : APIConstants.tags
      })
      .pipe(
        tap((resp) => {
          if (resp.body?.data) this._tags$.next(resp.body?.data);
        }),
        catchError((error) => {
          if (this.embedAuthService.isEmbedded())
            SharedMethods.checkAndSendMessageToParent(EmbedMessageToParentStatus.Error);
          return throwError(() => error);
        })
      );
  }

  getTagsList(): Observable<HttpResponse<APIResponse<Tag[]>>> {
    if (!this._tags$.value) {
      return this.getTagsListForce().pipe(tap((res) => this._tags$.next(res.body?.data ?? [])));
    } else {
      const resp = new HttpResponse<APIResponse<Tag[]>>({
        status: 200,
        body: { data: this._tags$.value } as APIResponse<Tag[]>
      });
      return of(resp);
    }
  }

  get tag$() {
    return this._tags$.asObservable();
  }

  getAllProceduresCode() {
    return this.http
      .sendSecureRequest<ProcedureCode[]>({
        method: HttpMethod.GET,
        url: this.embedAuthService.isEmbedded()
          ? APIConstants.externalApiEmbedProcedureCode
          : APIConstants.getProcedureCode,
        cache: true
      })
      .pipe(
        tap((response) => {
          if (response.ok && response.body) {
            this._procedureCodes$.next(response.body.data);
          }
        }),
        catchError((error) => {
          if (this.embedAuthService.isEmbedded())
            SharedMethods.checkAndSendMessageToParent(EmbedMessageToParentStatus.Error);
          return throwError(() => error);
        })
      );
  }

  get procedureCodes$() {
    return this._procedureCodes$.asObservable();
  }

  clearProviderCache() {
    const cacheKey = this.getProviderCacheKey();
    this._providersCache.delete(cacheKey);
    this._providers$.next([]);
  }

  getExtraSteps({ websiteUrl }: { websiteUrl: string }) {
    return this.http.sendSecureRequest<{ extraSteps: string }>({
      method: HttpMethod.GET,
      url: this.embedAuthService.isEmbedded()
        ? APIConstants.externalApiEmbedGetExtraSteps
        : APIConstants.getExtraSteps,
      params: new HttpParams({ fromObject: { websiteUrl } })
    });
  }

  updateGroupName(groupName: string) {
    return this.http.sendSecureRequest<GetGroupInfoResponse>({
      method: HttpMethod.PUT,
      url: APIConstants.updateGroupName,
      cache: true,
      params: new HttpParams({ fromObject: { groupId: this.authService.getCurrentLocation() } }),
      body: { groupName }
    });
  }

  getAllDXCPortals({ cache }: { cache: boolean }) {
    return this.http
      .sendSecureRequest<DXCPortal[]>({
        method: HttpMethod.GET,
        url: this.embedAuthService.isEmbedded()
          ? APIConstants.getAllDXCPortalsEmbed
          : APIConstants.getAllDXCPortals,
        cache
      })
      .pipe(
        catchError((error) => {
          if (this.embedAuthService.isEmbedded())
            SharedMethods.checkAndSendMessageToParent(EmbedMessageToParentStatus.Error);
          return throwError(() => error);
        })
      );
  }

  getDXCMFAServiceType(type: GroupMFAType): DXCMFAServiceType {
    return this.groupToDXCMFAMap[type];
  }

  getGroupMFAType(type: DXCMFAServiceType): GroupMFAType {
    return this.dxcToGroupMFAMap[type];
  }
}
